<template>
  <div class="multi-canteen-header">
    <h1 class="dashboard-title">{{ title }}</h1>

    <p class="scope-summary" aria-live="polite">
      <span class="badge badge-blue">{{ scopeBadgeText }}</span>
      <span v-if="hasAnyCanteen" class="scope-names">{{ scopeNamesText }}</span>
    </p>

    <fieldset v-if="hasAnyCanteen" class="scope-filter">
      <legend class="selector-label">🏢 Filtrer par cantine (facultatif)</legend>
      <ul class="canteen-checklist">
        <li v-for="canteen in allCanteens" :key="canteen.id">
          <label class="canteen-checkbox-label">
            <input type="checkbox" :value="canteen.id" v-model="selectedCanteenIds" />
            {{ canteen.name }}
          </label>
        </li>
      </ul>
      <button type="button" class="reset-filter-btn" :disabled="!isFiltered" @click="resetFilter">
        Afficher toutes mes cantines
      </button>
    </fieldset>

    <p v-else class="empty-state" role="status">Aucune cantine rattachée pour ce rôle : aucune donnée à afficher.</p>

    <slot name="extra" />
  </div>
</template>

<script>
export default {
  name: "MultiCanteenContextHeader",

  props: {
    title: {
      type: String,
      required: true,
    },
    // Rôle du dashboard (COOK, NUTRITIONIST, SURPLUSMANAGER…) : détermine quels rattachements
    // de `loggedUser.canteenMemberships` composent le périmètre. Ne provoque aucun appel réseau
    // propre au composant, il ne fait que lire ce que `fetchInitialData` a déjà chargé.
    job: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      // Vide = périmètre complet. Ne jamais y mettre de valeur par défaut autre que vide :
      // le filtre est facultatif, il ne doit rien restreindre tant que l'utilisateur n'a rien
      // choisi (cf. docs/design-system-dashboards.md et proposal.md « Arbitrages tranchés »).
      selectedCanteenIds: [],
    }
  },

  computed: {
    // Le périmètre vient des rattachements de l'utilisateur pour ce rôle, jamais d'un repli sur
    // le premier élément d'une liste ni sur une cantine inventée.
    allCanteens() {
      const user = this.$store.state.loggedUser
      const memberships = (user && user.canteenMemberships) || []
      const byId = new Map()
      memberships
        .filter((membership) => membership.job === this.job)
        .forEach((membership) => {
          if (!byId.has(membership.canteen)) {
            byId.set(membership.canteen, { id: membership.canteen, name: membership.canteenName })
          }
        })
      return Array.from(byId.values())
    },

    hasAnyCanteen() {
      return this.allCanteens.length > 0
    },

    isFiltered() {
      return this.selectedCanteenIds.length > 0
    },

    // Périmètre effectivement publié : le périmètre complet tant qu'aucune restriction n'est
    // choisie, restreint au sous-ensemble sélectionné sinon.
    scopeCanteens() {
      if (!this.isFiltered) return this.allCanteens
      return this.allCanteens.filter((canteen) => this.selectedCanteenIds.includes(canteen.id))
    },

    scopeBadgeText() {
      if (!this.hasAnyCanteen) return "Aucune cantine"
      if (!this.isFiltered) return `${this.allCanteens.length} cantine${this.allCanteens.length > 1 ? "s" : ""}`
      return `${this.scopeCanteens.length} sur ${this.allCanteens.length} cantines`
    },

    scopeNamesText() {
      return this.scopeCanteens.map((canteen) => canteen.name).join(", ")
    },
  },

  watch: {
    selectedCanteenIds() {
      this.emitScope()
    },
  },

  mounted() {
    this.emitScope()
  },

  methods: {
    resetFilter() {
      this.selectedCanteenIds = []
    },

    emitScope() {
      // Une liste vide dit « pas de filtre » au sens de l'API : le périmètre complet issu de
      // `canteens_for` s'applique côté serveur. Le filtre du client ne peut que le restreindre.
      this.$emit("scope-change", {
        job: this.job,
        canteenIds: this.selectedCanteenIds.slice(),
        canteens: this.scopeCanteens,
        isFiltered: this.isFiltered,
      })
    },
  },
}
</script>

<style scoped>
.multi-canteen-header {
  margin-bottom: 2rem;
  text-align: center;
}

.dashboard-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.25rem 0;
}

.scope-summary {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0.75rem 0;
}

.scope-names {
  font-size: 0.875rem;
  color: #6b7280;
}

.badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  white-space: nowrap;
}

.badge-blue {
  background: #dbeafe;
  color: #1e40af;
}

.scope-filter {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem 1.5rem;
  margin: 1rem auto 0;
  max-width: 640px;
}

.selector-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  padding: 0;
}

.canteen-checklist {
  list-style: none;
  margin: 0.75rem 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.5rem;
  justify-content: center;
}

.canteen-checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9375rem;
  color: #1a1a1a;
  cursor: pointer;
}

.canteen-checkbox-label input[type="checkbox"] {
  width: 1.125rem;
  height: 1.125rem;
  cursor: pointer;
}

.canteen-checkbox-label input[type="checkbox"]:focus-visible {
  outline: 3px solid #1d4ed8;
  outline-offset: 2px;
}

.reset-filter-btn {
  font-size: 0.875rem;
  font-weight: 500;
  color: #2563eb;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
}

.reset-filter-btn:hover:not(:disabled) {
  color: #1d4ed8;
  text-decoration: underline;
}

.reset-filter-btn:disabled {
  color: #9ca3af;
  cursor: not-allowed;
}

.reset-filter-btn:focus-visible {
  outline: 3px solid #1d4ed8;
  outline-offset: 2px;
}

.empty-state {
  color: #6b7280;
  font-size: 0.9375rem;
  margin: 1rem 0 0;
}
</style>
